import { StorageService } from './StorageService';

type StorageDataWithExpiration<T> = {
  data: T;
  expirationTime?: number; // timestamp in milliseconds
};

export class LocalStorageService implements StorageService {
  private localStorage: Storage;

  constructor() {
    this.localStorage = window.localStorage;
  }

  async setItem(key: string, value: string): Promise<void> {
    key && value && this.localStorage.setItem(key, value);
  }

  async getItem(key: string): Promise<string | null> {
    return this.localStorage.getItem(key);
  }

  async setObject<T>(key: string, value: T, expirationHours?: number): Promise<void> {
    if (key && value !== undefined && value !== null) {
      try {
        if (expirationHours) {
          const dataWithExpiration: StorageDataWithExpiration<T> = {
            data: value,
            expirationTime: Date.now() + expirationHours * 60 * 60 * 1000,
          };
          const serializedValue = JSON.stringify(dataWithExpiration);

          this.localStorage.setItem(key, serializedValue);
        } else {
          const serializedValue = JSON.stringify(value);

          this.localStorage.setItem(key, serializedValue);
        }
      } catch (error) {
        console.error('Failed to serialize object for localStorage:', error);
      }
    }
  }

  async getObject<T>(key: string): Promise<T | null> {
    try {
      const item = this.localStorage.getItem(key);
      if (item === null) {
        return null;
      }

      const parsedItem = JSON.parse(item);

      // Check if this is data with expiration metadata
      if (
        parsedItem &&
        typeof parsedItem === 'object' &&
        'data' in parsedItem &&
        'expirationTime' in parsedItem
      ) {
        const dataWithExpiration = parsedItem as StorageDataWithExpiration<T>;

        // Check if data has expired
        if (dataWithExpiration.expirationTime && Date.now() > dataWithExpiration.expirationTime) {
          console.log(`Data for key '${key}' has expired, removing from localStorage`);
          this.localStorage.removeItem(key);
          return null;
        }

        return dataWithExpiration.data;
      }

      // Return data as-is (backward compatible for data stored without expiration)
      return parsedItem as T;
    } catch (error) {
      console.error('Failed to parse object from localStorage:', error);
      return null;
    }
  }

  async updateObjectKey<T extends Record<string, any>>(
    key: string,
    updates: Partial<T>,
    expirationHours?: number,
  ): Promise<void> {
    try {
      const existingData = (await this.getObject<T>(key)) || ({} as T);
      const updatedData = { ...existingData, ...updates };

      // Save back to storage
      await this.setObject(key, updatedData, expirationHours);
    } catch (error) {
      console.error('Failed to update object key in localStorage:', error);
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      this.localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to remove item from localStorage:', error);
    }
  }
}
